/**
 * 마스터 데이터 고정 ID 상수
 * MASTER_DATA.md 기준 - 변경 시 동기화 필수!
 *
 * @see /docs/MASTER_DATA.md
 * @updated 2025-11-28
 */

// ========================================
// 카테고리 테이블 (8개)
// ========================================

/** Badge Categories (배지 카테고리) */
export const BADGE_CATEGORY_ID = {
    ACTIVITY: 1,    // 활동
    DIET: 2,        // 식단
    HEALTH: 3,      // 건강
    SOCIAL: 4,      // 소셜
    SPECIAL: 5,     // 스페셜
} as const;

/** Badge Rarities (배지 희귀도) */
export const BADGE_RARITY_ID = {
    COMMON: 1,      // 일반
    RARE: 2,        // 레어
    EPIC: 3,        // 에픽
    LEGENDARY: 4,   // 전설
} as const;

/** Activity Categories (활동 카테고리) */
export const ACTIVITY_CATEGORY_ID = {
    CARDIO: 1,          // 유산소
    STRENGTH: 2,        // 근력
    SPORTS: 3,          // 스포츠
    MEDITATION: 4,      // 명상
    DAILY_ACTIVITIES: 5, // 일상활동
} as const;

/** Points Catalog Categories (포인트 상점 카테고리) */
export const POINTS_CATALOG_CATEGORY_ID = {
    CREDITS: 1,                 // 크레딧
    PREMIUM_FEATURES: 2,        // 프리미엄 기능
    EXPERT_CONSULTATIONS: 3,    // 전문가 상담
    DISCOUNT_COUPONS: 4,        // 할인 쿠폰
    MERCHANDISE: 5,             // 굿즈
    DONATIONS: 6,               // 기부
} as const;

/** Challenge Types (챌린지 유형) */
export const CHALLENGE_TYPE_ID = {
    ACTIVITY: 1,    // 활동 챌린지
    DIET: 2,        // 식단 챌린지
    HEALTH: 3,      // 건강 챌린지
    SOCIAL: 4,      // 소셜 챌린지
} as const;

/** Challenge Duration Types (챌린지 기간 유형) */
export const CHALLENGE_DURATION_TYPE_ID = {
    DAILY: 1,       // 일일
    WEEKLY: 2,      // 주간
    MONTHLY: 3,     // 월간
    CUSTOM: 4,      // 사용자 정의
} as const;

/** Challenge Target Types (챌린지 목표 유형) */
export const CHALLENGE_TARGET_TYPE_ID = {
    WORKOUT_COUNT: 1,       // 운동 횟수
    CALORIES_BURNED: 2,     // 칼로리 소모
    STEPS: 3,               // 걸음 수
    DURATION: 4,            // 활동 시간
    WATER_INTAKE: 5,        // 수분 섭취
    WEIGHT_LOSS: 6,         // 체중 감량
} as const;

/** Ranking Levels (랭킹 레벨) */
export const RANKING_LEVEL_ID = {
    SPROUT: 1,      // 새싹 (0-99)
    BEGINNER: 2,    // 초보 (100-499)
    REGULAR: 3,     // 일반 (500-999)
    SKILLED: 4,     // 숙련자 (1000-2499)
    EXPERT: 5,      // 전문가 (2500-4999)
    MASTER: 6,      // 마스터 (5000-9999)
    CHAMPION: 7,    // 챔피언 (10000-19999)
    LEGEND: 8,      // 레전드 (20000-49999)
    MYTHIC: 9,      // 신화 (50000-99999)
    IMMORTAL: 10,   // 불멸 (100000+)
} as const;

// ========================================
// 타입 테이블 (6개)
// ========================================

/** Metric Types (건강 지표 타입) */
export const METRIC_TYPE_ID = {
    WEIGHT: 1,              // 체중 (kg)
    HEIGHT: 2,              // 신장 (cm)
    BODY_FAT: 3,            // 체지방률 (%)
    SYSTOLIC_BP: 4,         // 수축기혈압 (mmHg)
    DIASTOLIC_BP: 5,        // 이완기혈압 (mmHg)
    HEART_RATE: 6,          // 심박수 (bpm)
    BLOOD_GLUCOSE: 7,       // 혈당 (mg/dL)
    CHOLESTEROL: 8,         // 콜레스테롤 (mg/dL)
    SLEEP_HOURS: 9,         // 수면시간 (hours)
    BODY_TEMPERATURE: 10,   // 체온 (°C)
    OXYGEN_SATURATION: 11,  // 산소포화도 (%)
} as const;

/** Medication Types (약물 유형) */
export const MEDICATION_TYPE_ID = {
    PRESCRIPTION: 1,    // 처방약
    OTC: 2,             // 일반의약품
    SUPPLEMENT: 3,      // 건강보조식품
    HERBAL: 4,          // 한약
} as const;

/** Medication Frequencies (복용 빈도) */
export const MEDICATION_FREQUENCY_ID = {
    ONCE_DAILY: 1,          // 하루 1회
    TWICE_DAILY: 2,         // 하루 2회
    THREE_TIMES_DAILY: 3,   // 하루 3회
    AS_NEEDED: 4,           // 필요시
} as const;

/** Activity Record Types (활동 기록 타입) */
export const ACTIVITY_RECORD_TYPE_ID = {
    MANUAL_ENTRY: 1,    // 수동 입력
    WORKOUT_PLAN: 2,    // 운동 플랜
    CHALLENGE: 3,       // 챌린지
    STEP_COUNT: 4,      // 걸음 수 (Google Fit)
    DISTANCE: 5,        // 이동 거리 (Google Fit)
    CALORIES_BURNED: 6, // 소모 칼로리 (Google Fit)
} as const;

/** Goal Types (건강 목표 타입) */
export const GOAL_TYPE_ID = {
    DAILY_STEPS: 1,             // 일일 걸음 수
    WEEKLY_WORKOUT_TIME: 2,     // 주간 운동 시간
    DAILY_CALORIES_BURN: 3,     // 일일 칼로리 소모
    WEEKLY_WORKOUT_COUNT: 4,    // 주간 운동 횟수
    DAILY_WATER_INTAKE: 5,      // 일일 수분 섭취
    DAILY_PROTEIN: 6,           // 일일 단백질
    DAILY_CALORIES_INTAKE: 7,   // 일일 칼로리 섭취
    TARGET_WEIGHT: 8,           // 목표 체중
    TARGET_BODY_FAT: 9,         // 목표 체지방률
    TARGET_BLOOD_PRESSURE: 10,  // 목표 혈압
    DAILY_SLEEP_HOURS: 11,      // 일일 수면 시간
} as const;

/** Points Categories (포인트 적립 카테고리) */
export const POINTS_CATEGORY_ID = {
    EXERCISE: 1,        // 운동
    HEALTH_RECORD: 2,   // 건강기록
    CHALLENGE: 3,       // 챌린지
    SOCIAL_ACTIVITY: 4, // 소셜활동
    ATTENDANCE: 5,      // 출석
} as const;

// ========================================
// 활동 관련 (1개)
// ========================================

/** Activity Types (활동 유형) */
export const ACTIVITY_TYPE_ID = {
    WALKING: 1,             // 걷기
    RUNNING: 2,             // 달리기
    CYCLING: 3,             // 자전거
    SWIMMING: 4,            // 수영
    HIKING: 5,              // 등산
    WEIGHT_TRAINING: 6,     // 웨이트 트레이닝
    BODYWEIGHT: 7,          // 맨몸 운동
    CROSSFIT: 8,            // 크로스핏
    TENNIS: 9,              // 테니스
    BASKETBALL: 10,         // 농구
    SOCCER: 11,             // 축구
    YOGA: 12,               // 요가
    PILATES: 13,            // 필라테스
    CLEANING: 14,           // 청소
    CLIMBING_STAIRS: 15,    // 계단 오르기
} as const;

// ========================================
// 건강 데이터 (2개)
// ========================================

/** Symptom Categories (증상 카테고리) */
export const SYMPTOM_CATEGORY_ID = {
    PAIN: 1,            // 통증
    DIGESTIVE: 2,       // 소화기
    RESPIRATORY: 3,     // 호흡기
    SKIN: 4,            // 피부
    MENTAL_HEALTH: 5,   // 정신건강
} as const;

/** Food Categories (음식 카테고리) */
export const FOOD_CATEGORY_ID = {
    GRAINS: 1,      // 곡류
    VEGETABLES: 2,  // 채소
    FRUITS: 3,      // 과일
    PROTEIN: 4,     // 단백질
    DAIRY: 5,       // 유제품
    BEVERAGES: 6,   // 음료
    SNACKS: 7,      // 간식/디저트
    SOUPS: 8,       // 국/찌개
} as const;

// ========================================
// 게이미피케이션 (2개)
// ========================================

/** Badges (배지) */
export const BADGE_ID = {
    FIRST_WORKOUT: 1,           // 첫 운동
    WORKOUT_ENTHUSIAST: 2,      // 운동 마니아
    WORKOUT_VETERAN: 3,         // 운동 베테랑
    WORKOUT_MASTER: 4,          // 운동 마스터
    STREAK_7_DAYS: 5,           // 7일 연속
    STREAK_30_DAYS: 6,          // 한 달 연속
    STREAK_100_DAYS: 7,         // 100일 챌린저
    DIET_TRACKING_BEGINNER: 8,  // 식단 관리 입문
    BALANCED_DIET: 9,           // 균형잡힌 식사
    HYDRATION_CHAMPION: 10,     // 수분 챔피언
    HEALTH_CHECKUP: 11,         // 건강 체크업
    FIRST_FRIEND: 12,           // 첫 친구
    POPULAR_MEMBER: 13,         // 인기 회원
    EARLY_ADOPTER: 14,          // 얼리 어답터
} as const;

/** Achievement Items (업적 항목) */
export const ACHIEVEMENT_ITEM_ID = {
    COMPLETE_WORKOUT: 1,    // 운동 완료
    CALORIES_BURNED: 2,     // 칼로리 소모
    STEPS_WALKED: 3,        // 걸음 수
    LOG_MEAL: 4,            // 식사 기록
    WATER_INTAKE: 5,        // 물 섭취
    LOG_WEIGHT: 6,          // 체중 기록
    SLEEP_HOURS: 7,         // 수면 시간
    MEDITATION: 8,          // 명상
    COMPLETE_CHALLENGE: 9,  // 챌린지 완료
    MAINTAIN_STREAK: 10,    // 연속 기록 유지
} as const;

// ========================================
// Type Exports
// ========================================

export type BadgeCategoryId = typeof BADGE_CATEGORY_ID[keyof typeof BADGE_CATEGORY_ID];
export type BadgeRarityId = typeof BADGE_RARITY_ID[keyof typeof BADGE_RARITY_ID];
export type ActivityCategoryId = typeof ACTIVITY_CATEGORY_ID[keyof typeof ACTIVITY_CATEGORY_ID];
export type PointsCatalogCategoryId = typeof POINTS_CATALOG_CATEGORY_ID[keyof typeof POINTS_CATALOG_CATEGORY_ID];
export type ChallengeTypeId = typeof CHALLENGE_TYPE_ID[keyof typeof CHALLENGE_TYPE_ID];
export type ChallengeDurationTypeId = typeof CHALLENGE_DURATION_TYPE_ID[keyof typeof CHALLENGE_DURATION_TYPE_ID];
export type ChallengeTargetTypeId = typeof CHALLENGE_TARGET_TYPE_ID[keyof typeof CHALLENGE_TARGET_TYPE_ID];
export type RankingLevelId = typeof RANKING_LEVEL_ID[keyof typeof RANKING_LEVEL_ID];
export type MetricTypeId = typeof METRIC_TYPE_ID[keyof typeof METRIC_TYPE_ID];
export type MedicationTypeId = typeof MEDICATION_TYPE_ID[keyof typeof MEDICATION_TYPE_ID];
export type MedicationFrequencyId = typeof MEDICATION_FREQUENCY_ID[keyof typeof MEDICATION_FREQUENCY_ID];
export type ActivityRecordTypeId = typeof ACTIVITY_RECORD_TYPE_ID[keyof typeof ACTIVITY_RECORD_TYPE_ID];
export type GoalTypeId = typeof GOAL_TYPE_ID[keyof typeof GOAL_TYPE_ID];
export type PointsCategoryId = typeof POINTS_CATEGORY_ID[keyof typeof POINTS_CATEGORY_ID];
export type ActivityTypeId = typeof ACTIVITY_TYPE_ID[keyof typeof ACTIVITY_TYPE_ID];
export type SymptomCategoryId = typeof SYMPTOM_CATEGORY_ID[keyof typeof SYMPTOM_CATEGORY_ID];
export type FoodCategoryId = typeof FOOD_CATEGORY_ID[keyof typeof FOOD_CATEGORY_ID];
export type BadgeId = typeof BADGE_ID[keyof typeof BADGE_ID];
export type AchievementItemId = typeof ACHIEVEMENT_ITEM_ID[keyof typeof ACHIEVEMENT_ITEM_ID];
